"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  Checkbox,
  Notice,
  PillGroup,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, HINT, LABEL } from "@/components/tool-kit/styles";
import {
  type NormalizerView,
  UnicodeNormalizerResults,
} from "@/components/tools/qualitative-text/unicode-normalizer-results";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  DEFAULT_NORMALIZE_OPTIONS,
  describeChanges,
  detectForms,
  measure,
  type NormalForm,
  type NormalizeOptions,
  transformText,
} from "@/lib/tool-kit/qualitative-text/unicode-normalize";

/** English strings. A translated content file overrides these through `ui`. */
export const UNICODE_TEXT_NORMALIZER_STRINGS = {
  privacy:
    "Runs entirely in your browser. Your text and any file you open are never uploaded.",
  inputLabel: "Text to normalize",
  inputPlaceholder: "Paste text that looks the same but compares as different.",
  fileLabel: "Or open a text file (read as UTF-8)",
  sample: "Load a sample",
  clear: "Clear",
  formLabel: "Normalization form",
  forms: {
    none: "None (only the options below)",
    NFC: "NFC",
    NFD: "NFD",
    NFKC: "NFKC",
    NFKD: "NFKD",
  } as Record<NormalForm, string>,
  formHints: {
    none: "Normalization is off. Only the width and ligature options below are applied.",
    NFC: "Canonical composition: é is one code point. The form the web and most software expect. Keeps compatibility characters such as ﬁ, ² and ™.",
    NFD: "Canonical decomposition: é becomes e plus a combining accent. Keeps compatibility characters.",
    NFKC: "Compatibility decomposition, then canonical composition. Also turns ﬁ into fi, ² into 2, ™ into TM and full-width letters into ASCII. Information is lost.",
    NFKD: "Compatibility decomposition, with no recomposition. The most aggressive form: é also becomes e plus an accent.",
  } as Record<NormalForm, string>,
  passesLabel: "Also, before normalizing",
  fullWidth:
    "Full-width to half-width: Ａ１！ and the ideographic space become A1! and a normal space",
  ligatures: "Expand ligatures: ﬁ ﬂ ﬃ ﬆ and the Armenian ligatures",
  letterLigatures: "Split the letters Æ æ Œ œ Ĳ ĳ into two letters",
  emptyPrompt: "Paste some text or load a sample to normalize it.",
  results: "Normalized text",
  inForms: "Your text, as pasted, is already in: {forms}.",
  inNoForm: "Your text, as pasted, is in none of the four normalization forms.",
  statCodePoints: "Code points, before → after",
  statUnits: "UTF-16 units, before → after",
  statBytes: "UTF-8 bytes, before → after",
  arrow: "{before} → {after}",
  unchanged:
    "Nothing changed: the text already has the form and characters you asked for.",
  outputLabel: "Result",
  copy: "Copy",
  copied: "Copied!",
  download: "Download .txt",
  filename: "normalized-text.txt",
  changesHeading:
    "one: One piece of text changed|other: {count} pieces of text changed",
  moreChanges: "{count} more kinds of change are not shown.",
  colBefore: "Before",
  colBeforeCodes: "Code points before",
  colAfter: "After",
  colAfterCodes: "Code points after",
  colCount: "Times",
  colBytes: "UTF-8 bytes each",
  sampleText: "Café naïve ﬁnal ＡＢＣ１２３　ｶ Ω Å x² ™ … ② 각",
} as const;

const FORM_ORDER: NormalForm[] = ["NFC", "NFD", "NFKC", "NFKD", "none"];

export default function UnicodeTextNormalizerWidget() {
  const strings = useToolUi(UNICODE_TEXT_NORMALIZER_STRINGS);
  const [text, setText] = useState("");
  const [form, setForm] = useState<NormalForm>(DEFAULT_NORMALIZE_OPTIONS.form);
  const [fullWidth, setFullWidth] = useState(false);
  const [ligatures, setLigatures] = useState(false);
  const [letterLigatures, setLetterLigatures] = useState(false);
  const deferred = useDeferredValue(text);

  const options: NormalizeOptions = useMemo(
    () => ({ form, fullWidth, ligatures, letterLigatures }),
    [form, fullWidth, ligatures, letterLigatures]
  );
  const view: NormalizerView | null = useMemo(() => {
    if (deferred === "") {
      return null;
    }
    const output = transformText(deferred, options);
    return {
      output,
      before: measure(deferred),
      after: measure(output),
      forms: detectForms(deferred),
      changes: describeChanges(deferred, options),
    };
  }, [deferred, options]);

  async function openFile(file: File | undefined) {
    if (file) {
      setText(await file.text());
    }
  }

  return (
    <>
      <Notice>{strings.privacy}</Notice>
      <TextAreaField
        id="un-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        rows={8}
        value={text}
      />
      <div className="flex flex-wrap items-center gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.sampleText)}
          type="button"
        >
          {strings.sample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
        <label className={LABEL} htmlFor="un-file">
          {strings.fileLabel}
        </label>
        <input
          accept=".txt,.csv,.tsv,.md,.json,text/*"
          className="text-foreground text-sm"
          id="un-file"
          onChange={(event) => {
            openFile(event.target.files?.[0]);
            event.target.value = "";
          }}
          type="file"
        />
      </div>
      <PillGroup
        label={strings.formLabel}
        name="un-form"
        onChange={setForm}
        options={FORM_ORDER.map((value) => ({
          value,
          label: strings.forms[value],
        }))}
        value={form}
      />
      <p className={HINT}>{strings.formHints[form]}</p>
      <fieldset className="space-y-2">
        <legend className={LABEL}>{strings.passesLabel}</legend>
        <Checkbox
          checked={fullWidth}
          label={strings.fullWidth}
          onChange={setFullWidth}
        />
        <Checkbox
          checked={ligatures}
          label={strings.ligatures}
          onChange={setLigatures}
        />
        <Checkbox
          checked={letterLigatures}
          label={strings.letterLigatures}
          onChange={setLetterLigatures}
        />
      </fieldset>
      {view ? (
        <UnicodeNormalizerResults strings={strings} view={view} />
      ) : (
        <Notice>{strings.emptyPrompt}</Notice>
      )}
    </>
  );
}
